import React, { useState } from 'react';
import {
  Layers,
  CheckCircle,
  CheckCircle2,
  Clock,
  AlertCircle,
  Calendar,
  BarChart3,
  FileText,
  Edit,
  Target
} from 'lucide-react';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  GlassTabs,
  CircularProgress,
  SectionReveal,
} from '@/design-system';
import FeatureReadinessChecklist from './FeatureReadinessChecklist';
import FeatureAllocationsTab from './FeatureAllocationsTab';
import FeatureTimelineTab from './FeatureTimelineTab';
import type { Feature } from '@/types';

interface FeatureDetailModalProps {
  feature: Feature;
  onClose: () => void;
}

const FeatureDetailModal: React.FC<FeatureDetailModalProps> = ({
  feature,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState('overview');

  const getStatusBadgeVariant = (status: string): 'success' | 'warning' | 'danger' | 'info' => {
    switch (status) {
      case 'COMPLETE':
        return 'info';
      case 'SHOVEL_READY':
        return 'success';
      case 'IN_REFINEMENT':
        return 'warning';
      case 'NOT_READY':
      default:
        return 'danger';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETE':
        return CheckCircle2;
      case 'SHOVEL_READY':
        return CheckCircle;
      case 'IN_REFINEMENT':
        return Clock;
      case 'NOT_READY':
      default:
        return AlertCircle;
    }
  };

  const getReadinessColor = (score: number): 'emerald' | 'amber' | 'rose' => {
    if (score >= 80) return 'emerald';
    if (score >= 40) return 'amber';
    return 'rose';
  };

  const estimatedCost = (feature.estimatedTeamWeeks || 0) * 89 * 40; // Using blended rate
  const StatusIcon = getStatusIcon(feature.status);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <FileText className="w-4 h-4" /> },
    { id: 'readiness', label: 'Readiness', icon: <CheckCircle className="w-4 h-4" /> },
    { id: 'allocations', label: 'Allocations', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'timeline', label: 'Timeline', icon: <Calendar className="w-4 h-4" /> },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <SectionReveal delay={0}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-accent-cyan/20 to-accent-blue/20">
                <Layers className="w-6 h-6 text-accent-cyan" />
              </div>
              <h2 className="text-xl font-semibold text-[var(--text)]">{feature.name}</h2>
              <GlassBadge variant="info">Q{feature.targetQuarter}</GlassBadge>
              <GlassBadge variant={getStatusBadgeVariant(feature.status)}>
                <StatusIcon className="w-3.5 h-3.5 mr-1" />
                {feature.status.replace('_', ' ')}
              </GlassBadge>
            </div>
            {feature.description && (
              <p className="text-[var(--text)] text-sm leading-relaxed ml-[52px]">
                {feature.description}
              </p>
            )}
          </div>
        </div>
      </SectionReveal>

      {/* Key Metrics */}
      <SectionReveal delay={0.05}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Readiness Score - with CircularProgress */}
          <GlassCard variant="subtle" padding="sm" hover className="text-center">
            <div className="flex flex-col items-center">
              <CircularProgress
                value={feature.readinessScore}
                size="lg"
                color={getReadinessColor(feature.readinessScore)}
                showValue
              />
              <div className="text-xs text-[var(--muted)] mt-2">Readiness Score</div>
            </div>
          </GlassCard>

          {/* Estimated TWs - Blue */}
          <GlassCard variant="subtle" padding="sm" hover className="text-center">
            <div className="flex flex-col items-center justify-center h-full py-2">
              <div className="text-2xl font-bold text-accent-blue">
                {feature.estimatedTeamWeeks || 0}
              </div>
              <div className="text-xs text-[var(--muted)] mt-2">Estimated TWs</div>
            </div>
          </GlassCard>

          {/* Estimated Cost - Emerald */}
          <GlassCard variant="subtle" padding="sm" hover className="text-center">
            <div className="flex flex-col items-center justify-center h-full py-2">
              <div className="text-2xl font-bold text-accent-emerald">
                ${estimatedCost.toLocaleString()}
              </div>
              <div className="text-xs text-[var(--muted)] mt-2">Estimated Cost</div>
            </div>
          </GlassCard>

          {/* Target Quarter - Violet */}
          <GlassCard variant="subtle" padding="sm" hover className="text-center">
            <div className="flex flex-col items-center justify-center h-full py-2">
              <div className="text-2xl font-bold text-accent-violet">
                Q{feature.targetQuarter}
              </div>
              <div className="text-xs text-[var(--muted)] mt-2">Target Quarter</div>
            </div>
          </GlassCard>
        </div>
      </SectionReveal>

      {/* Tabs */}
      <SectionReveal delay={0.1}>
        <GlassTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </SectionReveal>

      {/* Tab Content */}
      <SectionReveal delay={0.15}>
        <div className="min-h-[300px]">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Basic Information */}
              <GlassCard variant="subtle" padding="md">
                <div className="flex items-center gap-2 mb-4">
                  <Target className="w-4 h-4 text-accent-blue" />
                  <h3 className="text-sm font-semibold text-[var(--text)]">Basic Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">EPIC</label>
                    <p className="text-sm text-[var(--text)] mt-1">
                      {feature.epic?.name || 'No EPIC'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Lead Program</label>
                    <p className="text-sm text-[var(--text)] mt-1">
                      {feature.leadProgram?.name || 'Unassigned'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Created</label>
                    <p className="text-sm text-[var(--text)] mt-1">
                      {new Date(feature.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Last Updated</label>
                    <p className="text-sm text-[var(--text)] mt-1">
                      {new Date(feature.updatedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </GlassCard>

              {/* Description */}
              {feature.description && (
                <GlassCard variant="subtle" padding="md">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="w-4 h-4 text-accent-violet" />
                    <h3 className="text-sm font-semibold text-[var(--text)]">Description</h3>
                  </div>
                  <p className="text-sm text-[var(--text)] leading-relaxed whitespace-pre-wrap">
                    {feature.description}
                  </p>
                </GlassCard>
              )}

              {/* Capacity & Financial Impact */}
              <GlassCard variant="subtle" padding="md">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="w-4 h-4 text-accent-emerald" />
                  <h3 className="text-sm font-semibold text-[var(--text)]">Capacity & Financial Impact</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Estimated Effort</label>
                    <p className="text-xl font-bold text-accent-blue mt-1">
                      {feature.estimatedTeamWeeks || 0} TWs
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Estimated Cost</label>
                    <p className="text-xl font-bold text-accent-emerald mt-1">
                      ${estimatedCost.toLocaleString()}
                    </p>
                    <p className="text-xs text-[var(--muted)]">Blended rate</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Target Delivery</label>
                    <p className="text-xl font-bold text-accent-violet mt-1">
                      Q{feature.targetQuarter} 2024
                    </p>
                  </div>
                </div>
              </GlassCard>
            </div>
          )}

          {activeTab === 'readiness' && (
            <div className="space-y-4">
              <GlassCard variant="subtle" padding="md">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-accent-emerald" />
                    <h3 className="text-sm font-semibold text-[var(--text)]">Feature Readiness Checklist</h3>
                  </div>
                  <div className="flex items-center gap-3">
                    <CircularProgress
                      value={feature.readinessScore}
                      size="sm"
                      color={getReadinessColor(feature.readinessScore)}
                      showValue
                    />
                    <GlassBadge variant={getStatusBadgeVariant(feature.status)}>
                      {feature.status.replace('_', ' ')}
                    </GlassBadge>
                  </div>
                </div>

                <FeatureReadinessChecklist
                  featureId={feature.id}
                  currentScore={feature.readinessScore}
                />
              </GlassCard>
            </div>
          )}

          {activeTab === 'allocations' && (
            <FeatureAllocationsTab feature={feature} />
          )}

          {activeTab === 'timeline' && (
            <FeatureTimelineTab feature={feature} />
          )}
        </div>
      </SectionReveal>

      {/* Action Buttons */}
      <SectionReveal delay={0.2}>
        <div className="flex items-center justify-end gap-3 pt-5 border-t border-[var(--border)]">
          <GlassButton variant="ghost" onClick={onClose}>
            Close
          </GlassButton>
          <GlassButton variant="primary" leftIcon={<Edit className="w-4 h-4" />}>
            Edit Feature
          </GlassButton>
        </div>
      </SectionReveal>
    </div>
  );
};

export default FeatureDetailModal;
